import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box } from '@mui/material';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';

// Animations
const floatAnimation = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%);
  }
  50% {
    transform: translate(-50%, -70%);
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.3;
  }
`;

const particleFloat = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
    opacity: 0;
  }
`;

const shineAnimation = keyframes`
  0% {
    transform: translateX(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) rotate(45deg);
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 20px;
  position: relative;
  background-color: #f5f5f5;
  overflow-x: hidden;
`;

const AnimationContainer = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  margin-bottom: 24px;
`;

const Shield = styled.div`
  font-size: 120px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.2;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  
  &::before {
    content: "🛡️";
  }
`;

const Coin = styled.div`
  font-size: 80px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  animation: ${floatAnimation} 3s ease-in-out infinite;
  
  &::before {
    content: "🌱";
  }
`;

const ParticlesContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const Particle = styled.div`
  position: absolute;
  background: #3B3486;
  border-radius: 50%;
  animation: ${particleFloat} 3s infinite;
`;

const Title = styled(Typography)`
  font-size: 48px;
  margin: 0 0 16px;
  background: linear-gradient(45deg, #3B3486, #864AF9);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientAnimation} 8s ease infinite;
`;

const Message = styled(Typography)`
  color: #666;
  margin: 16px 0 32px;
  max-width: 500px;
  font-size: 18px;
  line-height: 1.6;
  
  p {
    margin: 0.5em 0;
  }
`;

const Highlight = styled.span`
  color: #3B3486;
  font-weight: 600;
`;

const StyledButton = styled.button`
  padding: 16px 32px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 18px;
  background-color: #3B3486;
  color: white;
  border: none;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 4px 6px rgba(59, 52, 134, 0.1);
  position: relative;
  overflow: hidden;
  cursor: pointer;
  z-index: 2;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(59, 52, 134, 0.2);
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    animation: ${shineAnimation} 3s infinite;
  }
`;

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    const createParticles = () => {
      const container = document.querySelector('.particles-container');
      if (!container) return;

      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'particle';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;
        const size = Math.random() * 8 + 4;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;
        particle.style.animationDelay = `${Math.random() * 2}s`;
        particle.style.animationDuration = `${Math.random() * 2 + 3}s`;
        container.appendChild(particle);
      }
    };

    createParticles();
  }, []);

  return (
    <PageContainer>
      <ParticlesContainer className="particles-container" />
      
      <AnimationContainer>
        <Shield />
        <Coin />
      </AnimationContainer>

      <Title variant="h1">
        404 - {t('notFound.title')}
      </Title>
      
      <Message>
        <p>
          {t('notFound.lostMessage')}
        </p>
        <p>
          {t('notFound.securityMessage')}
        </p>
      </Message>

      <StyledButton onClick={() => navigate('/')}>
        {t('notFound.protectButton')}
      </StyledButton>
    </PageContainer>
  );
};

export default NotFound; 